import { useState, useMemo } from 'react'
import { X, ChevronDown, ChevronUp } from 'lucide-react'
import FaceHoverPreview from './FaceHoverPreview'
import { useFaceHover } from './useFaceHover'
import { faceCropUrl, signedSrc } from '@/utils/media'
import { useSignedUrls } from '@/utils/useSignedUrls'

interface ClusterSuggestionCardProps {
  suggestionId: string
  faceIds: string[]
  faceCount: number
  cropUrls: string[]
  representativeCropUrl: string
  avgSimilarity: number
  onAccept: (suggestionId: string, name?: string, faceIds?: string[]) => void
  onReject: (suggestionId: string) => void
  onAssignFace?: (faceId: string) => void
}

/** Build crop URL from face_id, matching the backend's _crop_url_from_face_id. */
function cropUrlFromFaceId(faceId: string): string {
  return faceCropUrl(faceId)
}

export default function ClusterSuggestionCard({
  suggestionId,
  faceIds,
  faceCount,
  cropUrls,
  representativeCropUrl,
  avgSimilarity,
  onAccept,
  onReject,
  onAssignFace,
}: ClusterSuggestionCardProps) {
  useSignedUrls()
  const [name, setName] = useState('')
  const [expanded, setExpanded] = useState(false)
  const [excludedFaceIds, setExcludedFaceIds] = useState<Set<string>>(new Set())
  const { hoverState, handleMouseEnter, handleMouseLeave, handlePreviewEnter, handlePreviewLeave } = useFaceHover()

  const remainingFaceIds = useMemo(
    () => faceIds.filter((id) => !excludedFaceIds.has(id)),
    [faceIds, excludedFaceIds]
  )

  const remainingCount = remainingFaceIds.length

  const handleExcludeFace = (faceId: string) => {
    setExcludedFaceIds((prev) => {
      const next = new Set(prev)
      next.add(faceId)
      return next
    })
  }

  const handleAccept = () => {
    if (remainingCount === 0) return
    // Only pass faceIds override if some faces were excluded
    const overrideFaceIds = excludedFaceIds.size > 0 ? remainingFaceIds : undefined
    onAccept(suggestionId, name.trim() || undefined, overrideFaceIds)
  }

  // If all faces excluded, auto-reject
  if (remainingCount === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-4 opacity-50">
        <p className="text-xs text-muted-foreground text-center">All faces removed from this suggestion.</p>
        <button
          onClick={() => onReject(suggestionId)}
          className="mt-2 mx-auto block text-xs text-red-400 hover:text-red-300"
        >
          Dismiss
        </button>
      </div>
    )
  }

  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <div className="flex items-start gap-3">
        {/* Representative face */}
        <div
          className="w-14 h-14 rounded-full overflow-hidden bg-secondary flex-shrink-0 cursor-zoom-in"
          onMouseEnter={(e) => handleMouseEnter(faceIds[0], signedSrc(representativeCropUrl), e.currentTarget)}
          onMouseLeave={handleMouseLeave}
        >
          <img
            src={signedSrc(representativeCropUrl)}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-sm font-medium text-foreground">
              {remainingCount} face{remainingCount !== 1 ? 's' : ''}
              {excludedFaceIds.size > 0 && (
                <span className="text-muted-foreground ml-1">
                  ({excludedFaceIds.size} removed)
                </span>
              )}
            </span>
            <span className="text-xs text-cyan-400 bg-cyan-400/10 px-1.5 py-0.5 rounded">
              {Math.round(avgSimilarity * 100)}% similar
            </span>
          </div>

          {/* Preview faces (collapsed) */}
          {!expanded && (
            <div className="flex gap-1 mb-2 items-center">
              {cropUrls.slice(0, 5).map((url, i) => {
                const fid = faceIds[i]
                if (fid && excludedFaceIds.has(fid)) return null
                return (
                  <div
                    key={i}
                    className="w-8 h-8 rounded overflow-hidden bg-secondary cursor-zoom-in"
                    onMouseEnter={(e) => fid && handleMouseEnter(fid, signedSrc(url), e.currentTarget)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <img src={signedSrc(url)} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </div>
                )
              })}
              {faceCount > 5 && (
                <button
                  onClick={() => setExpanded(true)}
                  className="w-8 h-8 rounded bg-secondary flex items-center justify-center hover:bg-secondary/80 transition-colors"
                  title="Show all faces"
                >
                  <span className="text-[10px] text-muted-foreground">+{remainingCount - Math.min(5, cropUrls.filter((_, i) => !excludedFaceIds.has(faceIds[i])).length)}</span>
                </button>
              )}
              <button
                onClick={() => setExpanded(true)}
                className="p-0.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors ml-1"
                title="Expand to see all faces"
              >
                <ChevronDown size={14} />
              </button>
            </div>
          )}

          {/* Expanded: all faces in a wrapped grid */}
          {expanded && (
            <div className="mb-2">
              <div className="flex items-center gap-1 mb-1.5">
                <button
                  onClick={() => setExpanded(false)}
                  className="p-0.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                  title="Collapse"
                >
                  <ChevronUp size={14} />
                </button>
                <span className="text-[10px] text-muted-foreground">
                  Click x on a face to remove it from this group
                </span>
              </div>
              <div className="flex flex-wrap gap-1">
                {faceIds.map((fid) => {
                  if (excludedFaceIds.has(fid)) return null
                  const url = cropUrlFromFaceId(fid)
                  return (
                    <div
                      key={fid}
                      className="relative group/face w-10 h-10 rounded overflow-hidden bg-secondary cursor-zoom-in"
                      onMouseEnter={(e) => handleMouseEnter(fid, url, e.currentTarget)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleExcludeFace(fid)
                        }}
                        className="absolute -top-0.5 -right-0.5 p-0.5 rounded-full bg-red-600/80 hover:bg-red-600 text-white opacity-0 group-hover/face:opacity-100 transition-opacity"
                        title="Remove from group"
                      >
                        <X size={8} />
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Name input + actions */}
          <div className="relative z-10 flex items-center gap-2 flex-wrap">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name this person..."
              className="w-40 px-2 py-1 rounded bg-secondary border border-border text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-cyan-500"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAccept()
              }}
            />
            <button
              onClick={handleAccept}
              className="px-3 py-1 text-xs font-medium rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white transition-colors"
            >
              Save Changes
            </button>
            <button
              onClick={() => onReject(suggestionId)}
              className="px-3 py-1 text-xs font-medium rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {hoverState && (
        <FaceHoverPreview
          cropUrl={hoverState.cropUrl}
          faceId={hoverState.faceId}
          triggerRect={hoverState.triggerRect}
          onMouseEnter={handlePreviewEnter}
          onMouseLeave={handlePreviewLeave}
          onRemove={(fid) => {
            setExcludedFaceIds(prev => new Set([...prev, fid]))
            handlePreviewLeave()
          }}
          onAssign={onAssignFace ? (fid) => {
            onAssignFace(fid)
            handlePreviewLeave()
          } : undefined}
        />
      )}
    </div>
  )
}
